

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const DRAFT_KEY = "inspection_report_draft";
const OFFLINE_IMAGES_KEY = "offline_images";
const DRAFT_METADATA_KEY = 'inspection_draft_metadata';

// Image management utilities
const generateImageId = () =>
  `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const generateDraftKey = (eventType, dateTime) => {
  const formattedDate = dateTime ? new Date(dateTime).toISOString().split('T')[0] : 'no-date';
  return `${eventType || 'unknown'}_${formattedDate}`;
};

const compressImage = (file, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      const MAX_WIDTH = 1200;
      const MAX_HEIGHT = 1200;

      let { width, height } = img;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = (width * MAX_HEIGHT) / height;
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, "image/jpeg", quality);
    };

    img.src = URL.createObjectURL(file);
  });
};

// Convert file to base64 and store locally
export const storeImageLocally = async (file, imageId) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Compress image first
      const compressedFile = await compressImage(file);

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const base64Data = reader.result;
          const offlineImages = JSON.parse(
            localStorage.getItem(OFFLINE_IMAGES_KEY) || "{}"
          );

          // Store image data locally
          offlineImages[imageId] = {
            id: imageId,
            base64: base64Data,
            originalName: file.name,
            size: compressedFile.size,
            type: compressedFile.type,
            storedAt: Date.now(),
            uploaded: false,
            cloudUrl: null,
            relativePath: null,
          };

          localStorage.setItem(
            OFFLINE_IMAGES_KEY,
            JSON.stringify(offlineImages)
          );

          resolve({
            id: imageId,
            name: file.name,
            url: base64Data, // Use base64 data URL for local preview
            localId: imageId,
            isLocal: true,
            uploaded: false,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      reject(error);
    }
  });
};

const base64ToBlob = (base64Data) => {
  const byteCharacters = atob(base64Data.split(",")[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: "image/jpeg" });
};

// Get local image
const getLocalImage = (imageId) => {
  const offlineImages = JSON.parse(
    localStorage.getItem(OFFLINE_IMAGES_KEY) || "{}"
  );
  const imageData = offlineImages[imageId];

  if (!imageData) return null;

  const blob = base64ToBlob(imageData.base64);
  return {
    id: imageId,
    url: URL.createObjectURL(blob),
    name: imageData.originalName,
    localId: imageId,
    isLocal: true,
    uploaded: imageData.uploaded,
    cloudUrl: imageData.cloudUrl,
  };
};

// Async thunk for processing images (store locally first)
export const processImagesAsync = createAsyncThunk(
  "inspection/processImages",
  async (files) => {
    const processedImages = [];

    for (const file of files.slice(0, 50)) {
      const imageId = generateImageId();
      const imageData = await storeImageLocally(
        file.originFileObj || file,
        imageId
      );
      processedImages.push(imageData);
    }

    return processedImages;
  }
);

export const saveDraftAsync = createAsyncThunk(
  "inspection/saveDraft",
  async (formData, { getState }) => {
    try {
      const { eventType, dateTime, reportType, user_id } = formData;

      if (!eventType || !dateTime || !reportType || !user_id) {
        throw new Error("eventType, dateTime, reportType, and user_id are required to save draft");
      }

      const draftKey = generateDraftKey(eventType, dateTime);
      const state = getState();
      const { gallery } = state.inspection;

      // Process gallery images with module structure
      const processedGallery = {};
      Object.entries(gallery).forEach(([moduleKey, images]) => {
        processedGallery[moduleKey] = images.map((img) => ({
          id: img.id,
          name: img.name,
          localId: img.localId || img.id,
          isLocal: img.isLocal || false,
          uploaded: img.uploaded || false,
          cloudUrl: img.cloudUrl || null,
        }));
      });

      // Process step images
      const processedSteps = formData.steps.map((step) => ({
        ...step,
        units: step.units.map((unit) => ({
          ...unit,
          images: Object.fromEntries(
            Object.entries(unit.images || {}).map(([category, image]) => [
              category,
              image
                ? {
                  id: image.id,
                  name: image.name,
                  localId: image.localId || image.id,
                  isLocal: image.isLocal || false,
                  uploaded: image.uploaded || false,
                  cloudUrl: image.cloudUrl || null,
                }
                : null,
            ])
          ),
        })),
      }));

      const draftData = {
        ...formData,
        gallery: processedGallery,
        steps: processedSteps,
        savedAt: new Date().toISOString(),
        draftKey,
        title: `${eventType} - ${new Date(dateTime).toLocaleDateString()}`,
      };

      // Get existing drafts
      const existingDrafts = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");

      // Save this draft
      existingDrafts[draftKey] = draftData;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(existingDrafts));

      // Update metadata for quick access
      const metadata = JSON.parse(localStorage.getItem(DRAFT_METADATA_KEY) || "[]");
      const existingIndex = metadata.findIndex((meta) => meta.draftKey === draftKey);

      const metaEntry = {
        draftKey,
        title: draftData.title,
        eventType,
        reportType,
        user_id,
        dateTime,
        savedAt: draftData.savedAt,
        itemCount:
          formData.steps?.reduce((acc, step) => acc + (step.units?.length || 0), 0) ||
          0,
      };

      if (existingIndex >= 0) {
        metadata[existingIndex] = metaEntry;
      } else {
        metadata.unshift(metaEntry); // Add to beginning for recent-first order
      }

      // Keep only last 20 drafts
      if (metadata.length > 20) {
        const removedMeta = metadata.splice(20);
        // Remove corresponding draft data
        removedMeta.forEach((meta) => {
          delete existingDrafts[meta.draftKey];
        });
        localStorage.setItem(DRAFT_KEY, JSON.stringify(existingDrafts));
      }

      localStorage.setItem(DRAFT_METADATA_KEY, JSON.stringify(metadata));

      return { draftData, metadata };
    } catch (error) {
      throw new Error("Failed to save draft: " + error.message);
    }
  }
);

export const loadDraftAsync = createAsyncThunk(
  "inspection/loadDraft",
  async (draftKey) => {
    try {

      const drafts = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}');
      const draftData = drafts[draftKey];

      if (!draftData) {
        throw new Error("Draft not found");
      }

      // Reconstruct gallery images with module structure
      const reconstructedGallery = {};
      if (draftData.gallery) {
        // Handle both old array format and new object format
        if (Array.isArray(draftData.gallery)) {
          // Old format - put everything in general
          reconstructedGallery.general = draftData.gallery
            .map((img) => {
              if (img.isLocal && img.localId) {
                const localImage = getLocalImage(img.localId);
                return localImage || img;
              }
              return img;
            })
            .filter(Boolean);
        } else {
          // New object format
          Object.entries(draftData.gallery).forEach(([moduleKey, images]) => {
            reconstructedGallery[moduleKey] = images
              .map((img) => {
                if (img.isLocal && img.localId) {
                  const localImage = getLocalImage(img.localId);
                  return localImage || img;
                }
                return img;
              })
              .filter(Boolean);
          });
        }
      }

      // Reconstruct step images
      const reconstructedSteps = draftData.steps.map((step) => ({
        ...step,
        units: step.units.map((unit) => ({
          ...unit,
          images: Object.fromEntries(
            Object.entries(unit.images || {}).map(([category, image]) => {
              if (!image) return [category, null];

              if (image.isLocal && image.localId) {
                const localImage = getLocalImage(image.localId);
                return [category, localImage || image];
              }
              return [category, image];
            })
          ),
        })),
      }));
      return {
        ...draftData,
        gallery: reconstructedGallery,
        steps: reconstructedSteps,
      };
    } catch (error) {
      throw new Error("Failed to load draft: " + error.message);
    }
  }
);

export const getDraftsListAsync = createAsyncThunk(
  "inspection/getDraftsList",
  async () => {
    try {
      const metadata = JSON.parse(localStorage.getItem(DRAFT_METADATA_KEY) || '[]');
      return metadata;
    } catch (error) {
      throw new Error("Failed to get drafts list: " + error.message);
    }
  }
);

// Delete specific draft
export const deleteDraftAsync = createAsyncThunk(
  "inspection/deleteDraft",
  async (draftKey) => {
    try {
      const drafts = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}');
      const metadata = JSON.parse(localStorage.getItem(DRAFT_METADATA_KEY) || '[]');

      // Remove from drafts
      delete drafts[draftKey];
      localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));

      // Remove from metadata
      const updatedMetadata = metadata.filter(meta => meta.draftKey !== draftKey);
      localStorage.setItem(DRAFT_METADATA_KEY, JSON.stringify(updatedMetadata));

      return { draftKey, metadata: updatedMetadata };
    } catch (error) {
      throw new Error("Failed to delete draft: " + error.message);
    }
  }
);

// Clear all drafts
export const clearAllDraftsAsync = createAsyncThunk(
  "inspection/clearAllDrafts",
  async () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(DRAFT_METADATA_KEY);
      return true;
    } catch (error) {
      throw new Error("Failed to clear all drafts: " + error.message);
    }
  }
);

const uploadImageToServer = async (imageId, uploadEndpoint, shipId) => {
  const offlineImages = JSON.parse(
    localStorage.getItem(OFFLINE_IMAGES_KEY) || "{}"
  );
  const imageData = offlineImages[imageId];

  if (!imageData) {
    throw new Error(`Image ${imageId} not found in local storage`);
  }

  if (imageData.uploaded) {
    return {
      cloudUrl: imageData.cloudUrl,
      relativePath: imageData.relativePath,
    };
  }

  try {
    const blob = base64ToBlob(imageData.base64);
    const formData = new FormData();
    formData.append("image", blob, imageData.originalName);
    formData.append("imageId", imageId);
    formData.append("shipId", shipId);
    formData.append("eventType", "inspection");

    const response = await fetch(uploadEndpoint, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const result = await response.json();
    const cloudUrl = result.url || result.path;
    const relativePath = result.relativePath;

    // Update local storage to mark as uploaded
    offlineImages[imageId] = {
      ...imageData,
      uploaded: true,
      cloudUrl: cloudUrl,
      relativePath: relativePath,
      uploadedAt: Date.now(),
    };
    localStorage.setItem(OFFLINE_IMAGES_KEY, JSON.stringify(offlineImages));

    return { cloudUrl, relativePath };
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

// Clear draft and cleanup
export const clearDraft = createAsyncThunk(
  "inspection/clearDraft",
  async () => {
    localStorage.removeItem(DRAFT_KEY);
    return null;
  }
);
export const fetchDraftsAsync = createAsyncThunk(
  "inspection/fetchDrafts",
  async ({ user_id, reportType }) => {
    try {
      const metadata = JSON.parse(localStorage.getItem(DRAFT_METADATA_KEY) || "[]");
      const drafts = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");

      // Filter metadata
      const filteredMetadata = metadata.filter(
        (meta) => meta.user_id === user_id && meta.reportType === reportType
      );

      // Map to include actual draft data
      const results = filteredMetadata.map((meta) => ({
        ...meta,
        draft: drafts[meta.draftKey] || null,
      }));

      return results;
    } catch (error) {
      throw new Error("Failed to fetch drafts: " + error.message);
    }
  }
);
const initialState = {
  formData: { steps: [] },
  gallery: { general: [] }, // Changed to object with module structure
  galleryConfig: {
    maxImages: 100,
    maxImageSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    autoOrganize: true,
  },
  draftsList: [],
  isDraftsListLoading: false,
  selectedDraftKey: null,
  isDraftAvailable: false,
  isLoading: false,
  isUploading: false,
  uploadProgress: { current: 0, total: 0 },
  error: null,
  lastSaved: null,
  isOnline: navigator.onLine,
  pendingUploads: 0,
};

const inspectionSlice = createSlice({
  name: "inspection",
  initialState,
  reducers: {
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    updateGallery: (state, action) => {
      state.gallery = action.payload;
    },
    updateSteps: (state, action) => {
      state.formData.steps = action.payload;
    },
    resetForm: (state) => {
      state.formData = initialState.formData;
      state.gallery = { general: [] };
      state.error = null;
      state.lastSaved = null;
      state.uploadProgress = { current: 0, total: 0 };
    },
    checkDraftAvailability: (state) => {
      const draft = localStorage.getItem(DRAFT_KEY);
      state.isDraftAvailable = !!draft;
    },
    setOnlineStatus: (state, action) => {
      state.isOnline = action.payload;
    },

    updatePendingUploads: (state) => {
      const offlineImages = JSON.parse(
        localStorage.getItem(OFFLINE_IMAGES_KEY) || "{}"
      );
      state.pendingUploads = Object.values(offlineImages).filter(
        (img) => !img.uploaded
      ).length;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // NEW ACTIONS FOR MODULE GALLERY
    initializeModuleGalleries: (state, action) => {
      const { modules } = action.payload;

      // Ensure general gallery exists
      if (!state.gallery.general) {
        state.gallery.general = [];
      }

      // Initialize empty arrays for each module if they don't exist
      modules.forEach(module => {
        if (!state.gallery[module.name]) {
          state.gallery[module.name] = [];
        }
      });
    },
    addImagesToModule: (state, action) => {
      const { module, images } = action.payload;

      // Always create a fresh array
      const currentImages = state.gallery[module]
        ? state.gallery[module].map(img => ({ ...img })) // deep clone existing
        : [];

      images.forEach(image => {
        const idx = currentImages.findIndex(i => i.id === image.id);
        if (idx === -1) {
          currentImages.push({ ...image }); // clone new object
        } else {
          currentImages[idx] = { ...image };
        }
      });

      state.gallery[module] = currentImages;
    }

    ,
    updateImageInModule: (state, action) => {
      const { module, imageId, updatedImageData } = action.payload;
      if (!state.gallery[module]) return;

      const imageIndex = state.gallery[module].findIndex(
        img => img.id === imageId
      );

      if (imageIndex !== -1) {
        // Update the existing image with new data
        state.gallery[module][imageIndex] = {
          ...state.gallery[module][imageIndex],
          ...updatedImageData
        };
      }
    },
    removeImageFromModule: (state, action) => {
      const { module, imageId } = action.payload;

      if (state.gallery[module]) {
        state.gallery[module] = state.gallery[module].filter(
          image => image.id !== imageId
        );
      }

      // Also remove from local storage if it's a local image
      const offlineImages = JSON.parse(
        localStorage.getItem(OFFLINE_IMAGES_KEY) || "{}"
      );

      if (offlineImages[imageId]) {
        delete offlineImages[imageId];
        localStorage.setItem(OFFLINE_IMAGES_KEY, JSON.stringify(offlineImages));
      }
    },

    moveImageBetweenModules: (state, action) => {
      const { imageId, fromModule, toModule } = action.payload;

      if (!state.gallery[fromModule] || !state.gallery[toModule]) return;

      const imageIndex = state.gallery[fromModule].findIndex(
        img => img.id === imageId
      );

      if (imageIndex !== -1) {
        const image = state.gallery[fromModule][imageIndex];

        // Remove from source module
        state.gallery[fromModule].splice(imageIndex, 1);

        // Add to target module if not already there
        const existsInTarget = state.gallery[toModule].some(
          img => img.id === imageId
        );
        if (!existsInTarget) {
          state.gallery[toModule].push(image);
        }
      }
    },
    updateGalleryConfig: (state, action) => {
      state.galleryConfig = { ...state.galleryConfig, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // Process images
      .addCase(processImagesAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(processImagesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add processed images to general gallery by default
        if (!state.gallery.general) {
          state.gallery.general = [];
        }
        action.payload.forEach(image => {
          const existingIndex = state.gallery.general.findIndex(
            img => img.id === image.id
          );
          if (existingIndex === -1) {
            state.gallery.general.push(image);
          }
        });
      })

      .addCase(fetchDraftsAsync.pending, (state) => {
        state.isDraftsListLoading = true;
        state.error = null;
      })
      .addCase(fetchDraftsAsync.fulfilled, (state, action) => {
        state.isDraftsListLoading = false;
        state.draftsList = action.payload;
      })
      .addCase(fetchDraftsAsync.rejected, (state, action) => {
        state.isDraftsListLoading = false;
        state.error = action.error.message;
      })

      .addCase(processImagesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveDraftAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveDraftAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastSaved = action.payload.draftData.savedAt;
        state.draftsList = action.payload.metadata;
        state.isDraftAvailable = true;
        state.selectedDraftKey = action.payload.draftData.draftKey;
      })
      .addCase(saveDraftAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(loadDraftAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadDraftAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.formData = {
            eventType: action.payload.eventType,
            dateTime: action.payload.dateTime,
            steps: action.payload.steps
          };
          state.gallery = action.payload.gallery || { general: [] };
          state.selectedDraftKey = action.payload.draftKey;
        }
      })
      .addCase(loadDraftAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getDraftsListAsync.pending, (state) => {
        state.isDraftsListLoading = true;
      })
      .addCase(getDraftsListAsync.fulfilled, (state, action) => {
        state.isDraftsListLoading = false;
        state.draftsList = action.payload;
        state.isDraftAvailable = action.payload.length > 0;
      })
      .addCase(getDraftsListAsync.rejected, (state, action) => {
        state.isDraftsListLoading = false;
        state.error = action.error.message;
      })
      // Clear draft
      .addCase(clearDraft.fulfilled, (state) => {
        state.isDraftAvailable = false;
        state.lastSaved = null;
      });
  },
});

export const {
  updateFormData,
  updateGallery,
  updateSteps,
  resetForm,
  checkDraftAvailability,
  setOnlineStatus,
  updatePendingUploads,
  setUploadProgress,
  setError,
  clearError,
  // NEW MODULE GALLERY ACTIONS
  initializeModuleGalleries,
  addImagesToModule,
  removeImageFromModule,
  autoOrganizeImages,
  moveImageBetweenModules,
  updateGalleryConfig, updateImageInModule
} = inspectionSlice.actions;

export default inspectionSlice.reducer;